// ADR-0004 D1: audio cues are synthesised in the browser, not shipped as
// files. One small function per cue, built from an `OscillatorNode` and a
// `GainNode` envelope. The shared `AudioContext` is created lazily and
// resumed on the first user gesture (iOS otherwise refuses to play).
// Every cue is a no-op when audio is unavailable, the user has muted cues,
// or the call site is in a non-browser environment.

type AudioCtxCtor = new () => AudioContext;
type AudioWindow = Window & {
  webkitAudioContext?: AudioCtxCtor;
  AudioContext?: AudioCtxCtor;
};

let _ctx: AudioContext | null = null;
let _muted = false;
let _resumeAttached = false;

/** Create/resume the shared AudioContext synchronously inside a user gesture.
 *  Exported so the runner can call it on first touch (T-035 B5 / iOS unlock).
 */
export function unlock(): AudioContext | null {
  if (typeof window === "undefined") return null;
  const w = window as AudioWindow;
  const Ctor = w.AudioContext ?? w.webkitAudioContext;
  if (!Ctor) return null;

  let created = false;
  if (!_ctx) {
    try {
      _ctx = new Ctor();
      created = true;
    } catch {
      return null;
    }
  }

  if (_ctx.state === "suspended" || _ctx.state === "interrupted") {
    void _ctx.resume().catch(() => undefined);
  }

  if (created) attachResumeOnGesture();
  return _ctx;
}

export function setMuted(muted: boolean): void {
  _muted = muted;
}

export function isMuted(): boolean {
  return _muted;
}

function attachResumeOnGesture(): void {
  if (_resumeAttached || typeof window === "undefined") return;
  _resumeAttached = true;
  const handler = () => {
    void _ctx?.resume().catch(() => undefined);
    window.removeEventListener("pointerdown", handler);
    window.removeEventListener("keydown", handler);
  };
  window.addEventListener("pointerdown", handler, { once: true });
  window.addEventListener("keydown", handler, { once: true });
}

interface PlaySpec {
  type: OscillatorType;
  freq: number;
  start: number;
  duration: number;
  gain: number;
  freqEnd?: number;
}

function play(spec: PlaySpec, when = 0): void {
  if (_muted) return;
  const ctx = unlock();
  if (!ctx) return;
  if (ctx.state !== "running") return;
  const startAt = ctx.currentTime + when;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = spec.type;
  osc.frequency.setValueAtTime(spec.freq, startAt);
  if (spec.freqEnd !== undefined) {
    osc.frequency.linearRampToValueAtTime(spec.freqEnd, startAt + spec.duration);
  }
  // Sharp attack (10 ms), linear decay to 0 by end of duration.
  gain.gain.setValueAtTime(0, startAt);
  gain.gain.linearRampToValueAtTime(spec.gain, startAt + 0.01);
  gain.gain.linearRampToValueAtTime(0, startAt + spec.duration);
  osc.connect(gain).connect(ctx.destination);
  osc.start(startAt);
  osc.stop(startAt + spec.duration + 0.01);
}

/** 40 ms square, 880 Hz, sharp decay — rep counted. */
export function tick(): void {
  play({ type: "square", freq: 880, start: 0, duration: 0.04, gain: 0.18 });
}

/** Two sines, 660 then 880 Hz, 250 ms total — set complete. */
export function setDone(): void {
  play({ type: "sine", freq: 660, start: 0, duration: 0.12, gain: 0.2 });
  play({ type: "sine", freq: 880, start: 0.13, duration: 0.12, gain: 0.2 });
}

/** 180 ms sine, 220 Hz, soft attack — rest start. */
export function restStart(): void {
  play({ type: "sine", freq: 220, start: 0, duration: 0.18, gain: 0.22 });
}

/** Single countdown tone for `n` ∈ {1,2,3}: 660/740/880 Hz, 90 ms. */
export function countTick(n: 1 | 2 | 3): void {
  const freq = n === 1 ? 660 : n === 2 ? 740 : 880;
  play({ type: "sine", freq, start: 0, duration: 0.09, gain: 0.2 });
}

/** 400 ms bright triangle, 1046 Hz — rest end. */
export function restEnd(): void {
  play({ type: "triangle", freq: 1046, start: 0, duration: 0.4, gain: 0.24 });
}

/** Short arpeggio, roughly 900 ms — level up. */
export function levelUp(): void {
  play({ type: "sine", freq: 660, start: 0, duration: 0.15, gain: 0.22 });
  play({ type: "sine", freq: 880, start: 0.15, duration: 0.15, gain: 0.22 });
  play({ type: "sine", freq: 1100, start: 0.3, duration: 0.18, gain: 0.22 });
  play({ type: "sine", freq: 1320, start: 0.48, duration: 0.4, gain: 0.24 });
}

/** 80 ms sine, 1320 Hz, quiet — workout start (the Start tap). T-098:
 *  this used to be the "voice heard" blip; voice control is gone, the
 *  Start tap kept the sound. */
export function startBlip(): void {
  play({ type: "sine", freq: 1320, start: 0, duration: 0.08, gain: 0.1 });
}
